'use client'

import { createContext, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { FaImage, FaVideo } from 'react-icons/fa'
import { FiCompass } from 'react-icons/fi'
import NavHint from './NavHint'

// Provided by CreateSimplified (which owns the Image/Video toggle) around the feed,
// so the empty state — built in Create.jsx and rendered here via {gallery} — can
// react to the current mode. Defaults to 'image' when no provider is present.
export const GenerateModeContext = createContext('image')

// Empty state passed to <GenerateFeed emptyState={...} />.
export default function GenerateEmptyState() {
  const { t } = useTranslation()
  const mode = useContext(GenerateModeContext)
  const isVideo = mode === 'video'
  return (
    // Top-aligned (not centered) so the whole block — including the Explore button —
    // stays visible on short viewports instead of dropping under the prompt card.
    // Padding uses the feed reserves published by CreateSimplified: clears the mobile
    // top bar and keeps a safe gap above the prompt card.
    <div
      className="min-h-full w-full flex items-start justify-center px-6"
      style={{
        paddingTop: 'calc(var(--feed-top-reserve, 72px) + 24px)',
        paddingBottom: 'calc(var(--feed-bottom-reserve, 148px) + 16px)',
      }}
    >
      <div className="text-center max-w-md">
        <div className="flex items-center justify-center gap-3 mb-4">
          {isVideo ? (
            <FaVideo className="text-white" size={26} />
          ) : (
            <FaImage className="text-white" size={26} />
          )}
          <span className="text-white text-3xl font-bold tracking-tight">
            {isVideo ? t('video') : t('image')}
          </span>
        </div>
        <p className="text-gray-400 text-base leading-relaxed mb-7">
          {isVideo ? (
            <>
              {t('generateEmptyVideoLine1', 'Describe a video with a prompt.')}
              <br />
              {t(
                'generateEmptyVideoLine2',
                'Bring it to life, then extend it when it’s done.',
              )}
            </>
          ) : (
            <>
              {t('generateEmptyLine1')}
              <br />
              {t('generateEmptyLine2')}
            </>
          )}
        </p>
        <NavHint
          href="/explore"
          icon={FiCompass}
          label={t('needIdeasExplore')}
        />
      </div>
    </div>
  )
}
